import React, { useEffect, useState, useRef } from 'react';
import { Building2, Mail, Phone, Calendar, AlertCircle } from 'lucide-react';
import { ExtendedBrand, BrandFormData, formatDate, getLenderBadgeColor } from '../BrandTypes';
import Select from "react-select";
import { getPartnersList } from '../../../services/merchant';
import { getCategories } from "../../../services/caregory";
import { fetchBucketsByPartner } from "../../../services/scheme";
import { getSchemeBuckets } from '../../../services/schemeBucket';

interface OptionType {
  label: string;
  value: string;
}

interface BasicDetailsTabProps {
  selectedBrand: ExtendedBrand;
  editData: BrandFormData;
  isEditing: boolean;
  errors: Record<string, string>;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | any
  ) => void;
  handleSave: () => void;
  isUpdating: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  canManageBrands: boolean;
  handleDeleteConfirm: () => void;
}

export const BasicDetailsTab: React.FC<BasicDetailsTabProps> = ({
  selectedBrand,
  editData,
  isEditing,
  errors,
  handleChange,
  handleSave,
  isUpdating,
  setIsEditing,
  canManageBrands,
  handleDeleteConfirm,
}) => {

  const [publicForOptions, setPublicForOptions] = useState<OptionType[]>([]);
  const [isLoadingPublicFor, setIsLoadingPublicFor] = useState(false);
  // ================================
  // CATEGORY & SUBCATEGORY STATES
  // ================================
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);

  const [subCategoryOptions, setSubCategoryOptions] = useState<{ value: string; label: string }[]>([]);
  const [subCategoryLoading, setSubCategoryLoading] = useState(false);


  const [assignedBucketNames, setAssignedBucketNames] = useState<string[]>([]);
  const [bucketOptions, setBucketOptions] = useState<OptionType[]>([]);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [selectedBucketIds, setSelectedBucketIds] = useState<string[]>([]);




  // ============================================================
  // FETCH EXISTING BUCKET NAMES FOR NORMAL MODE + PRE-SELECTING
  // ============================================================
  useEffect(() => {
    async function fetchAssigned() {
      try {
        if (!selectedBrand?.name) return;

        const names = await fetchBucketsByPartner(selectedBrand.name);

        setAssignedBucketNames(names); // names = ["TestBucket"]
      } catch (err) {
        console.error("Error fetching assigned bucket names:", err);
      }
    }

    fetchAssigned();
  }, [selectedBrand]);



  // ============================================================
  // FETCH ALL BUCKET OPTIONS FOR EDIT MODE
  // ============================================================
  useEffect(() => {
    if (!isEditing) return;

    const fetchBuckets = async () => {
      try {
        setBucketLoading(true);

        const res: any = await getSchemeBuckets({ page: 1, pageSize: 100 });
        const list = res?.data || [];

        const opts = list.map((b: any) => ({
          value: b.schemeBucketId,
          label: b.name,
        }));

        setBucketOptions(opts);

      } catch (error) {
        console.error("Error fetching scheme buckets:", error);
        setBucketOptions([]);
      } finally {
        setBucketLoading(false);
      }
    };

    fetchBuckets();
  }, [isEditing]);

  // ============================================================
  // PRESELECT EXISTING BUCKETS (convert names → IDs)
  // ============================================================
  useEffect(() => {
    if (!isEditing || bucketOptions.length === 0) return;

    const ids = bucketOptions
      .filter(opt => assignedBucketNames.includes(opt.label))
      .map(opt => opt.value);

    setSelectedBucketIds(ids);

    handleChange({
      target: {
        name: "schemeBucket",
        value: ids,
      },
    });

  }, [isEditing, bucketOptions, assignedBucketNames]);



  // useEffect(() => {
  //   if (categoryOptions.length && editData.category?.length) {
  //     // re-sync categories after options ready
  //     handleChange({
  //       target: { name: "category", value: [...editData.category] },
  //     });
  //   }
  // }, [categoryOptions]);


  // ================================
  // FETCH CATEGORIES WHEN EDIT MODE ENABLES
  // ================================
  useEffect(() => {
    if (!isEditing) return;

    const fetchCategories = async () => {
      try {
        setCategoryLoading(true);
        const response: any = await getCategories({});
        const categories =
          response?.data?.result?.data ||
          response?.result?.data ||
          response?.data ||
          [];

        const options = categories.map((cat: any) => ({
          value: cat.code,
          label: `${cat.code} - ${cat.name}`,
        }));

        setCategoryOptions(options);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setCategoryOptions([]);
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategories();
  }, [isEditing]);

  // ================================
  // FETCH SUBCATEGORIES (on category change)
  // ================================
  useEffect(() => {
    // 🔹 If no category selected → clear subcategories (UI + state)
    if (!editData?.category || editData.category.length === 0) {
      setSubCategoryOptions([]);

      handleChange({
        target: { name: "subCategory", value: [] },
      } as any);

      return;
    }

    const fetchSubCategories = async () => {
      try {
        setSubCategoryLoading(true);

        const categoryQuery = editData.category.join(",");
        const result: any = await getCategories({ categoryId: categoryQuery });

        const categories = Array.isArray(result)
          ? result
          : result?.data?.result?.data ||
          result?.result?.data ||
          result?.data ||
          [];

        const allSubs: string[] = [];
        categories.forEach((cat: any) => {
          if (Array.isArray(cat.subCategory)) {
            allSubs.push(...cat.subCategory);
          }
        });

        const uniqueSubs = [...new Set(allSubs)];
        const options = uniqueSubs.map((sub) => ({
          value: sub,
          label: sub,
        }));

        setSubCategoryOptions(options);

        // 🧼 Remove invalid selected subcategories from state
        if (editData.subCategory?.length) {
          const validSubs = new Set(options.map((o) => o.value));
          const filteredSubs = editData.subCategory.filter((sub) =>
            validSubs.has(sub)
          );

          if (filteredSubs.length !== editData.subCategory.length) {
            handleChange({
              target: {
                name: "subCategory",
                value: filteredSubs,
              },
            } as any);
          }
        }

      } catch (err) {
        console.error("Error fetching subcategories:", err);
        setSubCategoryOptions([]);
      } finally {
        setSubCategoryLoading(false);
      }
    };

    fetchSubCategories();
  }, [editData.category]);



  // ================================
  // CATEGORY HANDLER
  // ================================
  const handleCategoriesChange = (selected: any) => {
    const selectedValues = selected?.map((opt: any) => opt.value) || [];
    const isEvSelected = selectedValues.includes("E2V");

    // Update category first
    handleChange({ target: { name: "category", value: selectedValues } });

    // Then, if EV selected → auto-toggle isEv
    handleChange({
      target: {
        name: "isEv",
        type: "checkbox",
        value: isEvSelected,
        checked: isEvSelected,
      },
    });
  };

  // ================================
  // HANDLE SUBCATEGORY CHANGE
  // ================================
  const handleSubCategoryChange = (selected: any) => {
    const selectedValues = selected ? selected.map((opt: any) => opt.value) : [];
    handleChange({ target: { name: "subCategory", value: selectedValues } });
  };

  // ================================
  // FETCH "Public For" OPTIONS
  // ================================
  useEffect(() => {
    const fetchPublicFor = async () => {
      try {
        setIsLoadingPublicFor(true);
        const list = await getPartnersList();
        const options = list.map((item: string) => ({ value: item, label: item }));
        setPublicForOptions(options);
      } catch (error) {
        console.error('Error fetching Public For options:', error);
      } finally {
        setIsLoadingPublicFor(false);
      }
    };

    fetchPublicFor();
  }, []);

  // ✅ Sync publicFor prefill after options load
  useEffect(() => {
    if (publicForOptions.length && editData.publicFor?.length) {
      handleChange({
        target: { name: "publicFor", value: [...editData.publicFor] },
      });
    }
  }, [publicForOptions]);

  const isBrand = selectedBrand?.isBrand;
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h3 className="font-medium text-gray-700">Information</h3>
      </div>

      <div className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Name field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name {isEditing && <span className="text-red-500">*</span>}
            </label>
            {isEditing ? (
              <>
                <input
                  name="name"
                  type="text"
                  value={editData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
              </>
            ) : (
              <div className="flex items-center">
                <Building2 size={18} className="text-blue-500 mr-2" />
                <p className="font-medium text-gray-800">{selectedBrand.name}</p>
              </div>
            )}
          </div>

          {/* Group ID field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group ID {isEditing && <span className="text-red-500">*</span>}
            </label>
            {isEditing ? (
              <>
                <input
                  name="groupId"
                  type="text"
                  value={editData.groupId}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-lg cursor-not-allowed"
                  title="Group ID is automatically set to match the Brand Name"
                />
                <p className="mt-1 text-xs text-gray-500">Group ID automatically matches Brand Name</p>
              </>
            ) : (
              <div className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg inline-block">
                {selectedBrand.groupId}
              </div>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category {isEditing && <span className="text-red-500">*</span>}
            </label>
            {isEditing ? (
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={categoryOptions}
                value={
                  categoryOptions.filter((opt) =>
                    (editData.category || []).includes(opt.value)
                  )
                }
                onChange={handleCategoriesChange}
                isLoading={categoryLoading}
                placeholder="Select categories"
                noOptionsMessage={() => "No categories found"}
                classNamePrefix="react-select"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }), // fix dropdown behind modals
                }}
                menuPortalTarget={document.body}
              />
            ) : (
              <p className="font-medium text-gray-800">
                {selectedBrand.category?.length
                  ? selectedBrand.category.join(", ")
                  : "N/A"}
              </p>
            )}
          </div>

          {/* Subcategory */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subcategory {isEditing && <span className="text-red-500">*</span>}
            </label>
            {isEditing ? (
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={subCategoryOptions}
                value={
                  subCategoryOptions.filter((opt) =>
                    (editData.subCategory || []).includes(opt.value)
                  )
                }
                onChange={(selected: any) =>
                  handleChange({
                    target: {
                      name: "subCategory",
                      value: selected?.map((opt: any) => opt.value) || [],
                    },
                  })
                }
                isLoading={subCategoryLoading}
                placeholder="Select subcategories"
                noOptionsMessage={() => "No subcategories found"}
                classNamePrefix="react-select"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                menuPortalTarget={document.body}
              />
            ) : (
              <p className="font-medium text-gray-800">
                {selectedBrand.subCategory?.length
                  ? selectedBrand.subCategory.join(", ")
                  : "N/A"}
              </p>
            )}
          </div>

          {/* -------------------------------------------------------- */}
          {/* SCHEME BUCKET FIELD                                     */}
          {/* -------------------------------------------------------- */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scheme Buckets
            </label>

            {/* Normal Mode */}
            {!isEditing && (
              <p className="font-medium text-gray-800">
                {assignedBucketNames.length
                  ? assignedBucketNames.join(", ")
                  : "None"}
              </p>
            )}

            {/* Edit Mode */}
            {isEditing && (
              <Select
                isMulti
                closeMenuOnSelect={false}
                isLoading={bucketLoading}
                options={bucketOptions}
                value={bucketOptions.filter(opt =>
                  selectedBucketIds.includes(opt.value)
                )}
                onChange={(selected) => {
                  const ids = selected.map((s: any) => s.value);
                  setSelectedBucketIds(ids);
                  handleChange({
                    target: { name: "schemeBucket", value: ids },
                  });
                }}
                placeholder="Select scheme buckets"
                classNamePrefix="react-select"
              />
            )}
          </div>


          {/* Contact No field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Number 
            </label>
            {isEditing ? (
              <>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span className="text-gray-500">+91</span>
                  </div>
                  <input
                    name="contactNo"
                    type="tel"
                    value={editData.contactNo}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-3 py-2 border ${errors.contactNo ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    maxLength={10}
                  />
                </div>
                {errors.contactNo && <p className="mt-1 text-xs text-red-500">{errors.contactNo}</p>}
              </>
            ) : (
              <div className="flex items-center">
                <Phone size={18} className="text-blue-500 mr-2" />
                <p className="font-medium text-gray-800">+91 {selectedBrand.mobileNo}</p>
              </div>
            )}
          </div>

          {/* Contact Email field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Email 
            </label>
            {isEditing ? (
              <>
                <input
                  name="contactEmail"
                  type="email"
                  value={editData.contactEmail}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.contactEmail ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.contactEmail && <p className="mt-1 text-xs text-red-500">{errors.contactEmail}</p>}
              </>
            ) : (
              <div className="flex items-center">
                <Mail size={18} className="text-blue-500 mr-2" />
                <p className="font-medium text-gray-800">{selectedBrand.email}</p>
              </div>
            )}
          </div>

          {/* Lender field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lender
            </label>
            {isEditing ? (
              <select
                name="lender"
                value={editData.lender?.toLowerCase() || ""} // make sure value matches option
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a lender (optional)</option>
                <option value="cashe">Cashe</option>
                <option value="zype">Zype</option>
                <option value="general">General</option>
              </select>

            ) : (
              <div className="flex items-center">
                {selectedBrand.lender ? (
                  <span className={`px-2 py-1 rounded-full text-sm font-semibold inline-flex items-center w-min whitespace-nowrap ${getLenderBadgeColor(selectedBrand.lender)}`}>
                    {selectedBrand.lender}
                  </span>
                ) : (
                  <span className="text-gray-600 text-sm">No lender assigned</span>
                )}
              </div>
            )}
          </div>

          {/* EV Flag field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            {isEditing ? (
              <div className="flex items-center mt-1">
                <input
                  id="isEv"
                  name="isEv"
                  type="checkbox"
                  checked={editData.isEv}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isEv" className="ml-2 block text-sm text-gray-700">
                  Electric Vehicle (EV) brand
                </label>
              </div>
            ) : (
              <div className="flex items-center">
                {selectedBrand.isEv ? (
                  <span className="bg-green-200 text-green-900 px-2 py-1 rounded-full text-sm font-semibold border border-green-300">
                    EV
                  </span>
                ) : (
                  <span className="bg-yellow-200 text-yellow-900 px-2 py-1 rounded-full text-sm font-semibold border border-yellow-300">
                    Standard
                  </span>
                )}
              </div>
            )}
          </div>


          {isBrand && (
            <>
              {/* Public Brand field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Public Brand 
                </label>
                {isEditing ? (
                  <>
                    <select
                      name="isPublic"
                      value={editData.isPublic ? "true" : "false"} // boolean → string
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                    {errors.isPublic && (
                      <p className="mt-1 text-xs text-red-500">{errors.isPublic}</p>
                    )}
                  </>
                ) : (
                  <p className="font-medium text-gray-800">
                    {selectedBrand.isPublic ? "True" : "False"}
                  </p>
                )}
              </div>

              {/* Public For field */}
              <div>
                <label htmlFor="publicFor" className="block text-sm font-medium text-gray-700 mb-1">
                  Public For
                </label>
                {isEditing ? (
                  <Select
                    id="publicFor"
                    isMulti
                    options={publicForOptions}
                    value={(editData.publicFor || []).map(value =>
                      publicForOptions.find(option => option.value === value)
                    ).filter(Boolean)}
                    onChange={(newValue) => {
                      const selected = Array.isArray(newValue) ? newValue.map(o => o.value) : [];
                      handleChange({ target: { name: "publicFor", value: selected } });
                    }}
                    className={errors.publicFor ? "react-select-error" : ""}
                    classNamePrefix="react-select"
                    placeholder={isLoadingPublicFor ? "Loading partners..." : "Select Public For"}
                    isLoading={isLoadingPublicFor}
                    isDisabled={isLoadingPublicFor || !publicForOptions.length}
                    noOptionsMessage={() =>
                      isLoadingPublicFor ? "Loading partners..." : "No partners found"
                    }
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                ) : (
                  <p className="font-medium text-gray-800">
                    {(editData.publicFor && editData.publicFor.length) ? editData.publicFor.join(', ') : '-'}
                  </p>
                )}
                {errors.publicFor && (
                  <p className="ml-2 block text-sm text-gray-700 flex items-center">
                    <AlertCircle size={12} className="mr-1" />
                    {errors.publicFor}
                  </p>
                )}
              </div>
            </>
          )}


          {/* Dashboard Checkbox */}
          {/* Dashboard Access */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dashboard
            </label>
            {isEditing ? (
              <div className="flex items-center mt-1">
                <input
                  id="dashboard"
                  name="dashboard"
                  type="checkbox"
                  checked={!!editData.dashboard}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="dashboard" className="ml-2 block text-sm text-gray-700">
                  Dashboard access enabled
                </label>
              </div>
            ) : (
              <div className="text-gray-800 font-medium">
                {selectedBrand.dashboard ? "Dashboard access enabled" : "No dashboard access"}
              </div>
            )}
          </div>


          {/* Created At field (view only) */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Created On
              </label>
              <div className="flex items-center">
                <Calendar size={18} className="text-blue-500 mr-2" />
                <p className="font-medium text-gray-800">
                  {formatDate(selectedBrand.createdAt)}
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
